/**
 * UUID Session Management Utilities for Public Websearch Chatbot Client
 *
 * Implements client-side session management using browser session storage with UUIDs
 * as specified in the README.md requirements.
 */

import { generateUUID } from '@/lib/utils';
import type { ThreadMessage } from '@assistant-ui/react';

interface MessageMetadata {
  custom?: {
    sessionId?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface TypedThreadMessage extends ThreadMessage {
  metadata?: MessageMetadata;
}

interface MessageWrapper {
  message: TypedThreadMessage;
  parentId?: string;
}

interface ExportedMessages {
  messages: (TypedThreadMessage | MessageWrapper)[];
  [key: string]: unknown;
}

interface SessionStorageData {
  messages?: (TypedThreadMessage | MessageWrapper)[];
  timestamp?: number;
  [key: string]: unknown;
}

const SESSION_KEY = 'public-websearch-chatbot-session-id';
const SESSION_TIMEOUT_KEY = 'public-websearch-chatbot-session-timeout';
const MESSAGES_KEY = 'public-websearch-chatbot-messages';
const MODAL_OPEN_KEY = 'public-websearch-chatbot-modal-open';
const BUBBLE_DISMISSED_KEY = 'public-websearch-chatbot-bubble-dismissed';
const FEEDBACK_CHAT_ID_KEY = 'public-websearch-chatbot-feedback-chat-id';
const FEEDBACK_FORM_STATE_KEY = 'public-websearch-chatbot-feedback-form-state';
const DEFAULT_SESSION_TIMEOUT_MS = 3600 * 1000; // 1 hour in milliseconds
const DECIMAL_RADIX = 10; // Base 10 for parseInt

/**
 * Creates a new session with a random UUID and stores it in browser session storage
 * @returns {string} The new session ID
 */
export const createSession = (): string => {
  const sessionId = generateUUID();
  const timestamp = Date.now();

  try {
    sessionStorage.setItem(SESSION_KEY, sessionId);
    sessionStorage.setItem(SESSION_TIMEOUT_KEY, timestamp.toString());

    return sessionId;
  } catch (error) {
    // Fallback: return UUID even if storage fails
    return sessionId;
  }
};

/**
 * Gets the current session ID from browser session storage
 * @returns {string | null} The current session ID or null if no session exists
 */
export const getCurrentSessionId = (): string | null => {
  try {
    return sessionStorage.getItem(SESSION_KEY);
  } catch (error) {
    return null;
  }
};

/**
 * Gets the session creation timestamp
 * @returns {number | null} The timestamp when session was created or null
 */
export const getSessionTimestamp = (): number | null => {
  try {
    const timestamp = sessionStorage.getItem(SESSION_TIMEOUT_KEY);
    return timestamp ? parseInt(timestamp, DECIMAL_RADIX) : null;
  } catch (error) {
    return null;
  }
};

/**
 * Checks if the current session has expired based on timeout
 * @param {number} timeoutMs - Session timeout in milliseconds (default: 1 hour)
 * @returns {boolean} True if session has expired or doesn't exist
 */
export const isSessionExpired = (
  timeoutMs: number = DEFAULT_SESSION_TIMEOUT_MS,
): boolean => {
  const timestamp = getSessionTimestamp();

  if (!timestamp) {
    return true; // No session timestamp means expired/invalid
  }

  const now = Date.now();
  const elapsed = now - timestamp;

  return elapsed > timeoutMs;
};

/**
 * Checks if a valid session exists and is not expired
 * @param {number} timeoutMs - Session timeout in milliseconds (default: 1 hour)
 * @returns {boolean} True if valid session exists
 */
export const hasValidSession = (
  timeoutMs: number = DEFAULT_SESSION_TIMEOUT_MS,
): boolean => {
  const sessionId = getCurrentSessionId();

  if (!sessionId) {
    return false;
  }

  return !isSessionExpired(timeoutMs);
};

/**
 * Gets the current session ID or creates a new one if needed
 * Handles session expiration and automatic regeneration
 * @param {number} timeoutMs - Session timeout in milliseconds (default: 1 hour)
 * @returns {string} The current or new session ID
 */
export const getOrCreateSession = (
  timeoutMs: number = DEFAULT_SESSION_TIMEOUT_MS,
): string => {
  const currentSessionId = getCurrentSessionId();

  // If no session exists, create new one
  if (!currentSessionId) {
    return createSession();
  }

  // If session has expired, create new one
  if (isSessionExpired(timeoutMs)) {
    clearSession(); // Clean up old session
    return createSession();
  }

  // Session is valid, return it
  return currentSessionId;
};

/**
 * Clears the current session from browser session storage
 * Used for session resets as per README requirements
 * Also performs cleanup of old/expired sessions to prevent accumulation
 */
export const clearSession = (): void => {
  try {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_TIMEOUT_KEY);

    // Optional: Clear old message data to prevent memory accumulation
    // Only if we want aggressive cleanup
    // sessionStorage.removeItem(MESSAGES_KEY);
    // sessionStorage.removeItem(MODAL_OPEN_KEY);
  } catch (error) {
    // Silently handle storage errors
  }
};

/**
 * Resets the session by clearing current and creating new
 * Used for session reset triggers as per README:
 * - User feedback submission
 * - Inactivity timeout
 * - Browser events (tab close, restart)
 * - API error conditions
 * @returns {string} The new session ID
 */
export const resetSession = (): string => {
  clearSession();
  return createSession();
};

/**
 * Adds sessionId ONLY to messages that don't already have one
 * Used during message save to ensure backward compatibility with legacy messages
 * IMPORTANT: This preserves existing sessionIds and only adds to truly new messages
 * @param {ExportedMessages | SessionStorageData | any} messages - The message data structure to enhance
 * @param {string} sessionId - The session ID to add to messages without sessionId
 * @returns {ExportedMessages | SessionStorageData | any} Enhanced message data with sessionId metadata (preserves existing sessionIds)
 */
export const addSessionIdToMessages = (
  messages: ExportedMessages | SessionStorageData | any,
  sessionId: string,
): ExportedMessages | SessionStorageData | any => {
  if (!messages || !sessionId) {
    return messages;
  }

  try {
    // Use a more explicit approach to avoid JSON.parse/stringify issues
    const enhanceMessage = (msg: any): any => {
      if (!msg || typeof msg !== 'object') {
        return msg;
      }

      // IMPORTANT: Handle message wrapper structure from @assistant-ui/react
      // The structure is: { message: { ...actualMessage }, parentId: ... }
      // We need to check for sessionId in msg.message.metadata.custom, not msg.metadata.custom

      const actualMessage = msg.message || msg; // Handle both wrapper and direct message
      const existingSessionId = actualMessage.metadata?.custom?.sessionId;

      if (existingSessionId) {
        // Preserve existing sessionId from other sessions
        return msg;
      }

      // Add sessionId to messages that don't have one

      // If it's a message wrapper, add sessionId to the INNER message metadata
      if (msg.message) {
        const enhanced = {
          ...msg,
          message: {
            ...msg.message,
            metadata: {
              ...msg.message.metadata,
              custom: {
                ...msg.message.metadata?.custom,
                sessionId: sessionId,
              },
            },
          },
        };
        return enhanced;
      }

      // If it's a direct message, add sessionId to the message itself
      return {
        ...msg,
        metadata: {
          ...msg.metadata,
          custom: {
            ...msg.metadata?.custom,
            sessionId: sessionId,
          },
        },
      };
    };

    const enhanceMessageArray = (messageArray: any[]): any[] => {
      return messageArray.map(enhanceMessage);
    };

    // Handle different message data structures
    if (Array.isArray(messages)) {
      return enhanceMessageArray(messages);
    }

    if (messages.messages && Array.isArray(messages.messages)) {
      return {
        ...messages,
        messages: enhanceMessageArray(messages.messages),
      };
    }

    // For other structures, deep clone and look for message arrays
    const result = { ...messages };
    Object.keys(result).forEach((key) => {
      if (key === 'messages' && Array.isArray(result[key])) {
        result[key] = enhanceMessageArray(result[key]);
      }
    });

    return result;
  } catch (error) {
    console.warn(
      'Failed to add sessionId to messages, returning original:',
      error,
    );
    return messages;
  }
};

/**
 * Enhanced saveMessages that adds sessionId only to NEW messages without sessionId
 * This handles the case where user messages are created by @assistant-ui/react internally
 * @param {ExportedMessages | SessionStorageData | any} exportedMessages - The exported messages from ThreadRuntime
 */
export const saveMessages = (
  exportedMessages: ExportedMessages | SessionStorageData | any,
): void => {
  const sessionId = getCurrentSessionId();

  // Create a temporary session if none exists for saving purposes
  const effectiveSessionId = sessionId || getOrCreateSession();

  try {
    // Only add sessionId to messages that truly don't have one (new user messages)
    // Preserve existing sessionIds from previous sessions
    let enhancedMessages = exportedMessages;

    // Check if any messages need sessionId (likely new user messages)
    let needsEnhancement = false;
    if (enhancedMessages?.messages) {
      enhancedMessages.messages.forEach((msg: any) => {
        const actualMessage = msg.message || msg;
        if (!actualMessage.metadata?.custom?.sessionId) {
          needsEnhancement = true;
        }
      });
    }

    // Only enhance if there are truly new messages without sessionId
    if (needsEnhancement) {
      enhancedMessages = addSessionIdToMessages(
        exportedMessages,
        effectiveSessionId,
      );
    }

    sessionStorage.setItem(MESSAGES_KEY, JSON.stringify(enhancedMessages));
  } catch (error) {
    console.warn('Failed to save messages to session storage:', error);
  }
};

/**
 * Loads exported messages for the current session from session storage
 * @returns {ExportedMessages | SessionStorageData | null} The exported messages or null if not found
 */
export const loadMessages = ():
  ExportedMessages | SessionStorageData | null => {
  // Load messages regardless of current session - messages should persist across sessions
  try {
    const saved = sessionStorage.getItem(MESSAGES_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.warn('Failed to load messages from session storage:', error);
    return null;
  }
};

/**
 * Saves modal open state to session storage
 * @param {boolean} isOpen - Whether the modal is open
 */
export const saveModalState = (isOpen: boolean): void => {
  try {
    sessionStorage.setItem(MODAL_OPEN_KEY, JSON.stringify(isOpen));
  } catch (error) {
    console.warn('Failed to save modal state to session storage:', error);
  }
};

/**
 * Loads modal open state from session storage
 * @returns {boolean} The modal open state, defaults to false
 */
export const loadModalState = (): boolean => {
  try {
    const saved = sessionStorage.getItem(MODAL_OPEN_KEY);
    return saved ? JSON.parse(saved) : false;
  } catch (error) {
    console.warn('Failed to load modal state from session storage:', error);
    return false;
  }
};

/**
 * Marks the attention bubble as dismissed for this session
 * Used to hide the bubble after:
 * - User clicks the close button
 * - User opens the modal
 */
export const dismissBubble = (): void => {
  try {
    sessionStorage.setItem(BUBBLE_DISMISSED_KEY, 'true');
  } catch (error) {
    console.warn('Failed to save bubble dismissed state:', error);
  }
};

/**
 * Checks if attention bubble was dismissed in this session
 * @returns {boolean} True if bubble was dismissed
 */
export const isBubbleDismissed = (): boolean => {
  try {
    return sessionStorage.getItem(BUBBLE_DISMISSED_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

/**
 * Saves the feedback chatId (old session ID) to session storage
 * Used when user clicks feedback button - saves the old chatId before resetting session
 * @param {string} chatId - The chat/session ID to associate with feedback
 */
export const saveFeedbackChatId = (chatId: string): void => {
  try {
    sessionStorage.setItem(FEEDBACK_CHAT_ID_KEY, chatId);
  } catch (error) {
    console.warn('Failed to save feedback chatId to session storage:', error);
  }
};

/**
 * Gets the temporary feedback chatId from session storage
 * This is the old session ID that feedback should be submitted against
 * @returns {string | null} The feedback chatId or null if not set
 */
export const getFeedbackChatId = (): string | null => {
  try {
    return sessionStorage.getItem(FEEDBACK_CHAT_ID_KEY);
  } catch (error) {
    console.warn('Failed to get feedback chatId from session storage:', error);
    return null;
  }
};

/**
 * Clears the temporary feedback chatId from session storage
 * Should be called after feedback is successfully submitted
 */
export const clearFeedbackChatId = (): void => {
  try {
    sessionStorage.removeItem(FEEDBACK_CHAT_ID_KEY);
  } catch (error) {
    console.warn(
      'Failed to clear feedback chatId from session storage:',
      error,
    );
  }
};

/**
 * Feedback form state interface
 */
interface FeedbackFormState {
  messageId: string;
  feedbackType: number;
  feedbackChatId: string;
}

/**
 * Saves the feedback form state to session storage
 * Used to persist form visibility across page reloads
 * @param {string} messageId - The message ID the feedback is for
 * @param {number} feedbackType - The type of feedback (1=SOLVED, 2=NOT_HELPFUL)
 * @param {string} feedbackChatId - The chat ID for feedback submission
 */
export const saveFeedbackFormState = (
  messageId: string,
  feedbackType: number,
  feedbackChatId: string,
): void => {
  try {
    const state: FeedbackFormState = {
      messageId,
      feedbackType,
      feedbackChatId,
    };
    sessionStorage.setItem(FEEDBACK_FORM_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn(
      'Failed to save feedback form state to session storage:',
      error,
    );
  }
};

/**
 * Loads the feedback form state from session storage
 * @returns {FeedbackFormState | null} The feedback form state or null if not found
 */
export const loadFeedbackFormState = (): FeedbackFormState | null => {
  try {
    const saved = sessionStorage.getItem(FEEDBACK_FORM_STATE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.warn(
      'Failed to load feedback form state from session storage:',
      error,
    );
    return null;
  }
};

/**
 * Clears the feedback form state from session storage
 * Should be called when form is submitted or feedback is reset
 */
export const clearFeedbackFormState = (): void => {
  try {
    sessionStorage.removeItem(FEEDBACK_FORM_STATE_KEY);
  } catch (error) {
    console.warn(
      'Failed to clear feedback form state from session storage:',
      error,
    );
  }
};

/**
 * Session manager interface for centralized session operations
 */
export const SessionManager = {
  generateUUID,
  createSession,
  getCurrentSessionId,
  getSessionTimestamp,
  isSessionExpired,
  hasValidSession,
  getOrCreateSession,
  clearSession,
  resetSession,
  addSessionIdToMessages,
  saveMessages,
  loadMessages,
  saveModalState,
  loadModalState,
  dismissBubble,
  isBubbleDismissed,
  saveFeedbackChatId,
  getFeedbackChatId,
  clearFeedbackChatId,
  saveFeedbackFormState,
  loadFeedbackFormState,
  clearFeedbackFormState,
} as const;
